// **React Imports
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'

// **Components Imports
import IconifyIcon from '../icon'

// ** Constants
const PLACEHOLDER_MESSAGES = [
  "Ask AI or press '/' for commands",
  'Tip: Only one slash command is allowed per message',
  'Tip: You can use the slash command only at the start of the message',
  'Tip: Press Shift + Enter to add a new line',
  "Use '/' commands for quick workflows & easy access",
  "Press '/' to schedule interviews, reject, or invite",
  'Ask anything about candidate evaluations & scores'
]

const ZWSP = '\u200B'

const CommandChipInput = ({
  value,
  onChange,
  onKeyDown,
  disabled,
  placeholder,
  inputRef: externalInputRef,
  activeCommand,
  endAdornment
}) => {
  // states
  const [placeholderIdx, setPlaceholderIdx] = useState(0)

  // refs
  const editorRef = useRef(null)
  const prevActiveCommandRef = useRef(null)
  const chipRootRef = useRef(null)

  // variables
  const isInputEmpty = useMemo(() => {
    if (!value) {
      return true
    }
    return value?.replace(/\u200B/g, '') === ''
  }, [value])

  const shellClassName = useMemo(() => {
    return `chatbot-ce-shell${disabled ? ' chatbot-ce-shell--disabled' : ''}`
  }, [disabled])

  const editorClassName = useMemo(() => {
    return `chatbot-ce-editor${disabled ? ' chatbot-ce-editor--disabled' : ''}`
  }, [disabled])

  // Rotate animated placeholder every 4 seconds when input is empty
  useEffect(() => {
    if (value) return
    const timer = setInterval(() => {
      setPlaceholderIdx((prev) => (prev + 1) % PLACEHOLDER_MESSAGES.length)
    }, 4000)
    return () => clearInterval(timer)
  }, [value])

  // Merge the forwarded ref so the parent can still call inputRef.current.focus()
  const mergedRef = useCallback(
    (node) => {
      editorRef.current = node
      if (!externalInputRef) return
      if (typeof externalInputRef === 'function') externalInputRef(node)
      else externalInputRef.current = node
    },
    [externalInputRef]
  )

  // helpers

  const extractPlainText = useCallback(() => {
    const el = editorRef.current
    if (!el) return ''
    let text = ''
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent.replace(/\u200B/g, '')
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.dataset?.commandChip === 'true') {
          text += node.dataset.command ?? ''
        } else if (node.tagName === 'BR') {
          text += '\n'
        } else {
          node?.childNodes?.forEach(walk)
        }
      }
    }
    el.childNodes.forEach(walk)

    if (text.endsWith('\n')) {
      text = text.slice(0, -1)
    }
    return text
  }, [])

  const moveCaretToEnd = useCallback(() => {
    const el = editorRef.current

    if (!el) {
      return
    }

    el.focus()

    const sel = window.getSelection()

    if (!sel) {
      return
    }

    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)

    sel.removeAllRanges()
    sel.addRange(range)
  }, [])

  const handleRemoveChip = useCallback(
    (cmd) => {
      const el = editorRef.current

      if (!el) {
        return
      }

      const text = extractPlainText()
      let remainder = ''

      if (cmd && text?.startsWith(cmd?.command)) {
        remainder = text?.slice(cmd?.command?.length)?.trimStart()
      }

      el.innerHTML = ''

      prevActiveCommandRef.current = null

      if (remainder) {
        el.appendChild(document.createTextNode(remainder))
        requestAnimationFrame(moveCaretToEnd)
      }

      onChange?.({ target: { value: remainder } })
    },
    [extractPlainText, moveCaretToEnd, onChange]
  )

  const buildChipNode = useCallback((cmd, onRemove) => {
    if (chipRootRef.current) {
      try {
        chipRootRef.current.unmount()
      } catch (e) {
        // ignore
      }
      chipRootRef.current = null
    }

    const span = document.createElement('span')
    span.contentEditable = 'false'
    span.dataset.commandChip = 'true'
    span.dataset.command = cmd.command
    span.className = 'command-chip'

    const root = createRoot(span)
    chipRootRef.current = root
    root.render(
      <span className="command-chip__inner">
        <IconifyIcon
          icon={cmd.icon || 'mdi:slash-forward-box'}
          style={{
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            color: 'inherit'
          }}
          className="command-chip__icon"
        />
        <span className="command-chip__label">{cmd.command}</span>
        <span
          role="button"
          tabIndex={0}
          aria-label="Remove command"
          className="command-chip__remove"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onRemove?.(cmd)
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          <IconifyIcon
            icon="mdi:close"
            style={{
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center'
            }}
          />
        </span>
      </span>
    )

    return span
  }, [])

  // handlers
  const handleInput = useCallback(() => {
    const text = extractPlainText()
    if (!text && editorRef.current && editorRef.current?.innerHTML !== '') {
      editorRef.current.innerHTML = ''
      prevActiveCommandRef.current = null
    }

    onChange?.({ target: { value: text } })
  }, [extractPlainText, onChange])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Backspace' && editorRef.current) {
        const el = editorRef.current
        const chipEl = el.querySelector('[data-command-chip]')
        if (chipEl) {
          const text = extractPlainText()
          const afterCmd = text
            ?.slice(chipEl?.dataset?.command?.length || 0)
            ?.trim()
          const sel = window.getSelection()
          if (
            !afterCmd ||
            (sel &&
              sel.anchorNode === chipEl.nextSibling &&
              sel.anchorOffset === 0) ||
            (sel && sel.anchorNode === el && sel.anchorOffset <= 1)
          ) {
            e.preventDefault()
            el.innerHTML = ''
            prevActiveCommandRef.current = null
            onChange?.({ target: { value: '' } })
            return
          }
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
      }
      onKeyDown?.(e)
    },
    [extractPlainText, onChange, onKeyDown]
  )

  const handlePaste = useCallback((e) => {
    e.preventDefault()
    const text = e.clipboardData?.getData('text/plain') ?? ''
    if (!text) return
    document.execCommand('insertText', false, text)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
  }, [])

  // effects
  useEffect(() => {
    return () => {
      if (chipRootRef.current) {
        try {
          chipRootRef.current.unmount()
        } catch (e) {
          // ignore
        }
        chipRootRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const el = editorRef.current
    if (!el) {
      return
    }

    if (value === '') {
      if (el.innerHTML !== '') {
        el.innerHTML = ''
        prevActiveCommandRef.current = null
      }
      return
    }

    const prevCmd = prevActiveCommandRef.current
    const nextCmd = activeCommand

    if (prevCmd?.command === nextCmd?.command) {
      const domText = extractPlainText()

      if (domText === value) return

      if (nextCmd) {
        const chipEl = el.querySelector('[data-command-chip]')
        if (chipEl) {
          const afterCmd = value.slice(nextCmd.command.length)
          while (chipEl.nextSibling) el.removeChild(chipEl.nextSibling)
          el.appendChild(document.createTextNode(afterCmd || ZWSP))
          requestAnimationFrame(moveCaretToEnd)
        }
        return
      }

      // No chip + value differs → rebuild plain text
      el.innerHTML = ''
      el.appendChild(document.createTextNode(value))
      requestAnimationFrame(moveCaretToEnd)
      return
    }

    prevActiveCommandRef.current = nextCmd

    if (nextCmd) {
      const afterCmd = value.slice(nextCmd.command.length)
      el.innerHTML = ''
      el.appendChild(buildChipNode(nextCmd, handleRemoveChip))
      el.appendChild(document.createTextNode(afterCmd || ZWSP))
      requestAnimationFrame(moveCaretToEnd)
    } else {
      el.innerHTML = ''
      el.appendChild(document.createTextNode(value))
      requestAnimationFrame(moveCaretToEnd)
    }
  }, [
    value,
    activeCommand,
    extractPlainText,
    buildChipNode,
    handleRemoveChip,
    moveCaretToEnd
  ])

  return (
    <div className={shellClassName}>
      {isInputEmpty && (
        <div className="chatbot-ce-placeholder" aria-hidden="true">
          <span key={placeholderIdx} className="chatbot-ce-placeholder__text">
            {PLACEHOLDER_MESSAGES[placeholderIdx]}
          </span>
        </div>
      )}

      <div
        ref={mergedRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck
        role="textbox"
        aria-label={placeholder}
        aria-multiline="true"
        aria-disabled={disabled}
        data-placeholder={placeholder}
        className={editorClassName}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onDrop={handleDrop}
      />

      <div className="chatbot-ce-adornment">{endAdornment}</div>
    </div>
  )
}

export default memo(CommandChipInput)
